import { useMutation } from "@tanstack/react-query";
import { isValid } from "date-fns";
import { Archive, ArchiveRestore, Pencil } from "lucide-react";
import {
  InfiniteListBase,
  ShowBase,
  useDataProvider,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useShowContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState } from "react";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
import { SplitFlap } from "../misc/SplitFlap";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { DealEdit } from "./DealEdit";
import { ContactList } from "./ContactList";
import { findDealLabel, formatISODateString } from "./dealUtils";
import { compactAmountOptions, formatDealAmount } from "../misc/currency";

// ── Mobile full-page show ────────────────────────────────────────────────────

export const DealShowPage = () => (
  <ShowBase>
    <DealShowPageContent />
  </ShowBase>
);

const DealShowPageContent = () => {
  const { record, isPending } = useShowContext<Deal>();
  const translate = useTranslate();
  const { dealStages, dealCategories, currency } = useConfigurationContext();
  const [editOpen, setEditOpen] = useState(false);
  const [locale = "en"] = useLocaleState();

  if (isPending) return <DealShowPageSkeleton />;
  if (!record) return null;

  const isArchived = !!record.archived_at;
  const stageLabel = findDealLabel(dealStages, record.stage) ?? record.stage;
  const stageColor =
    record.stage === "won"
      ? "var(--nd-orange)"
      : record.stage === "lost"
        ? "var(--nd-accent)"
        : "var(--nd-text-secondary)";

  const amount = formatDealAmount(record, currency, locale, {
    notation: "compact",
    maximumFractionDigits: (record.amount ?? 0) >= 1_000_000 ? 1 : 0,
  });

  const closingDateValid = isValid(new Date(record.expected_closing_date));
  const isPast =
    closingDateValid && new Date(record.expected_closing_date) < new Date();

  return (
    <>
      <DealEdit open={editOpen} id={editOpen ? String(record.id) : undefined} />
      <MobileHeader>
        <MobileBackButton to="/deals" />
        <h1 className="font-nd-grotesk text-sm font-semibold text-nd-text-primary flex-1 truncate min-w-0 mx-2">
          {record.name}
        </h1>
        {!isArchived && (
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full shrink-0"
            aria-label={translate("ra.action.edit")}
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="size-5" />
          </Button>
        )}
      </MobileHeader>

      <MobileContent>
        {isArchived && <ArchivedTitle />}

        {/* Hero: amount + stage + company */}
        <div className="py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <SplitFlap
              value={amount.native}
              className="font-nd-mono text-4xl font-medium tabular-nums"
              style={{ color: stageColor }}
            />
            {amount.reporting && (
              <p className="font-nd-mono text-xs tabular-nums mt-1 text-nd-text-secondary">
                {amount.reporting}
              </p>
            )}
            <p
              className="font-nd-mono text-[10px] tracking-[0.08em] uppercase mt-1.5"
              style={{ color: stageColor }}
            >
              {stageLabel}
            </p>
          </div>
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
            className="shrink-0 mt-1"
          >
            <CompanyAvatar />
          </ReferenceField>
        </div>

        <div style={{ borderTop: "1px solid var(--nd-border)" }} />

        {/* Metadata grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 py-5">
          <DealMetaField
            label={translate("resources.deals.fields.expected_closing_date")}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm">
                {closingDateValid
                  ? formatISODateString(record.expected_closing_date)
                  : translate("resources.deals.invalid_date")}
              </span>
              {isPast && !isArchived && (
                <Badge variant="destructive">
                  {translate("crm.common.past")}
                </Badge>
              )}
            </div>
          </DealMetaField>

          {record.category && (
            <DealMetaField label={translate("resources.deals.fields.category")}>
              <span className="text-sm">
                {dealCategories.find((c) => c.value === record.category)
                  ?.label ?? record.category}
              </span>
            </DealMetaField>
          )}
        </div>

        {/* Contacts */}
        {!!record.contact_ids?.length && (
          <>
            <div style={{ borderTop: "1px solid var(--nd-border)" }} />
            <div className="py-5">
              <p className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled mb-3">
                {translate("resources.deals.fields.contact_ids")}
              </p>
              <ReferenceArrayField
                source="contact_ids"
                reference="contacts_summary"
              >
                <ContactList />
              </ReferenceArrayField>
            </div>
          </>
        )}

        {/* Description */}
        {record.description && (
          <>
            <div style={{ borderTop: "1px solid var(--nd-border)" }} />
            <div className="py-5">
              <p className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled mb-2">
                {translate("resources.deals.fields.description")}
              </p>
              <p className="font-nd-grotesk text-sm leading-6 whitespace-pre-line">
                {record.description}
              </p>
            </div>
          </>
        )}

        {/* Archive / delete */}
        <div style={{ borderTop: "1px solid var(--nd-border)" }} />
        <div className="py-5 flex gap-3">
          {isArchived ? (
            <>
              <UnarchiveButton record={record} />
              <DeleteButton />
            </>
          ) : (
            <ArchiveButton record={record} />
          )}
        </div>

        {/* Notes */}
        <div style={{ borderTop: "1px solid var(--nd-border)" }} />
        <div className="py-5">
          <InfiniteListBase
            resource="deal_notes"
            filter={{ deal_id: record.id }}
            sort={{ field: "date", order: "DESC" }}
            perPage={25}
            disableSyncWithLocation
            storeKey={false}
            empty={<NoteCreate reference="deals" />}
          >
            <NotesIterator reference="deals" />
          </InfiniteListBase>
        </div>
      </MobileContent>
    </>
  );
};

const DealMetaField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
      {label}
    </span>
    <div className="font-nd-grotesk">{children}</div>
  </div>
);

const DealShowPageSkeleton = () => (
  <>
    <MobileHeader>
      <div className="size-5 shrink-0" />
      <div
        className="h-3 w-32 rounded animate-pulse mx-2"
        style={{ background: "var(--nd-surface-raised)" }}
      />
    </MobileHeader>
    <MobileContent>
      <div className="py-5 space-y-3">
        <div
          className="h-10 w-28 rounded animate-pulse"
          style={{ background: "var(--nd-surface-raised)" }}
        />
        <div
          className="h-3 w-16 rounded animate-pulse"
          style={{ background: "var(--nd-surface-raised)" }}
        />
      </div>
      <div
        className="grid grid-cols-2 gap-6 py-5"
        style={{ borderTop: "1px solid var(--nd-border)" }}
      >
        {[1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <div
              className="h-2 w-16 rounded animate-pulse"
              style={{ background: "var(--nd-surface-raised)" }}
            />
            <div
              className="h-4 w-24 rounded animate-pulse"
              style={{ background: "var(--nd-surface-raised)" }}
            />
          </div>
        ))}
      </div>
    </MobileContent>
  </>
);

// ── Desktop dialog show ───────────────────────────────────────────────────────

export const DealShow = ({ open, id }: { open: boolean; id?: string }) => {
  const redirect = useRedirect();
  const handleClose = () => {
    redirect("list", "deals");
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="lg:max-w-4xl p-4 overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        {id ? (
          <ShowBase id={id}>
            <DealShowContent />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const DealShowContent = () => {
  const translate = useTranslate();
  const { dealStages, dealCategories } = useConfigurationContext();
  const record = useRecordContext<Deal>();
  if (!record) return null;

  return (
    <>
      <div className="space-y-2">
        {record.archived_at ? <ArchivedTitle /> : null}
        <div className="flex-1">
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              >
                <CompanyAvatar />
              </ReferenceField>
              <h2 className="font-nd-grotesk text-2xl font-semibold truncate">
                {record.name}
              </h2>
            </div>
            <div className={`flex gap-2 ${record.archived_at ? "" : "pr-12"}`}>
              {record.archived_at ? (
                <>
                  <UnarchiveButton record={record} />
                  <DeleteButton />
                </>
              ) : (
                <>
                  <ArchiveButton record={record} />
                  <EditButton />
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 m-4">
            <div className="flex flex-col mr-10">
              <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                {translate("resources.deals.fields.expected_closing_date")}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-nd-grotesk text-sm">
                  {isValid(new Date(record.expected_closing_date))
                    ? formatISODateString(record.expected_closing_date)
                    : translate("resources.deals.invalid_date")}
                </span>
                {new Date(record.expected_closing_date) < new Date() ? (
                  <Badge variant="destructive">
                    {translate("crm.common.past")}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col mr-10">
              <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                {translate("resources.deals.fields.amount")}
              </span>
              <DealAmountValue record={record} />
            </div>

            {record.category && (
              <div className="flex flex-col mr-10">
                <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                  {translate("resources.deals.fields.category")}
                </span>
                <span className="font-nd-grotesk text-sm">
                  {dealCategories.find((c) => c.value === record.category)
                    ?.label ?? record.category}
                </span>
              </div>
            )}

            <div className="flex flex-col mr-10">
              <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                {translate("resources.deals.fields.stage")}
              </span>
              <span className="font-nd-grotesk text-sm">
                {findDealLabel(dealStages, record.stage)}
              </span>
            </div>
          </div>

          {!!record.contact_ids?.length && (
            <div className="m-4">
              <div className="flex flex-col min-h-12 mr-10">
                <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                  {translate("resources.deals.fields.contact_ids")}
                </span>
                <ReferenceArrayField
                  source="contact_ids"
                  reference="contacts_summary"
                >
                  <ContactList />
                </ReferenceArrayField>
              </div>
            </div>
          )}

          {record.description && (
            <div className="m-4 whitespace-pre-line">
              <span className="font-nd-mono text-[10px] tracking-[0.08em] uppercase text-nd-text-disabled">
                {translate("resources.deals.fields.description")}
              </span>
              <p className="font-nd-grotesk text-sm leading-6">
                {record.description}
              </p>
            </div>
          )}

          <div className="m-4">
            <Separator className="mb-4" />
            <InfiniteListBase
              resource="deal_notes"
              filter={{ deal_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={<NoteCreate reference={"deals"} />}
            >
              <NotesIterator reference="deals" />
            </InfiniteListBase>
          </div>
        </div>
      </div>
    </>
  );
};

const ArchivedTitle = () => {
  const translate = useTranslate();
  return (
    <div className="bg-orange-500 px-6 py-4">
      <h3 className="text-lg font-bold text-white">
        {translate("resources.deals.archived.title")}
      </h3>
    </div>
  );
};

const ArchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleClick = () => {
    update(
      "deals",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          redirect("list", "deals");
          notify("resources.deals.archived.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.archived.error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <Archive className="w-4 h-4" />
      {translate("resources.deals.archived.action")}
    </Button>
  );
};

const UnarchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate } = useMutation({
    mutationFn: () => dataProvider.unarchiveDeal(record),
    onSuccess: () => {
      redirect("list", "deals");
      notify("resources.deals.unarchived.success", {
        type: "info",
        undoable: false,
      });
      refresh();
    },
    onError: () => {
      notify("resources.deals.unarchived.error", {
        type: "error",
      });
    },
  });

  const handleClick = () => {
    mutate();
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <ArchiveRestore className="w-4 h-4" />
      {translate("resources.deals.unarchived.action")}
    </Button>
  );
};

/** Amount in the deal's currency with the reporting equivalent when foreign (ADR-010). */
const DealAmountValue = ({ record }: { record: Deal }) => {
  const { currency } = useConfigurationContext();
  const [locale = "en"] = useLocaleState();
  const amount = formatDealAmount(
    record,
    currency,
    locale,
    compactAmountOptions,
  );
  return (
    <span className="font-nd-mono text-sm tabular-nums">
      {amount.native}
      {amount.reporting && (
        <span className="text-nd-text-secondary"> · {amount.reporting}</span>
      )}
    </span>
  );
};
